import React, { MutableRefObject } from 'react';

import L from 'leaflet';
import { Wgs84Coordinates, CrzpCoordinates } from '../../models';
import { EvidenceAPI } from '../../enums';
import { CrzpMap } from './hook/map-hook';
import { MarkerPopupConfig } from './hook/marker-popup-factory';

/**
 * Props for the main <Map> component.
 */
export interface MapProps {
  api?: EvidenceAPI; // API endpoint or configuration for fetching map data
  width?: string;
  height?: string | number;
  defaultZoom?: number;
  defaultWgs84Position?: Wgs84Coordinates | Wgs84Coordinates[];
  children?: React.ReactNode; // For potential map overlays or controls rendered as children
  // Props for point adding mode (used in LocationPanel)
  onPointAdded?: (coords: { lat: number; lon: number }) => void;
  mode?: 'POINT_ADD' | 'PREVIEW';
}

/**
 * Enum for different types of base tile layers.
 */
export enum LayerType {
  BASE = 'BASE',
  WINTER = 'WINTER',
  AERIAL = 'AERIAL',
  OUTDOOR = 'OUTDOOR',
}

// --- Marker Component Types ---

/**
 * Enum for different visual types of markers.
 */
export enum MarkerType {
  DEFAULT = 'DEFAULT', // small red
  BIG_RED = 'BIG_RED',
  BIG_GREY = 'BIG_GREY',
  FILLED_GREY = 'FILLED_GREY',
  FILLED_BLUE = 'FILLED_BLUE',
  FILLED_RED = 'FILLED_RED',
  // legacy
  //CUSTOM_RED = 'CUSTOM_RED', // Custom red marker (e.g., with text)
  //CUSTOM_GREY = 'CUSTOM_GREY', // Custom grey marker (e.g., with text)
}

// --- Context Types ---

/**
 * Value provided by the main MapContext.
 */
export interface MapContextValue {
  map: MutableRefObject<CrzpMap | null>;
  isLoaded: boolean;
}

export interface SuggestDataItem {
  id: number | string;
  title: string;
  secondRow: string;
  thirdRow?: string;
  latitude: number;
  longitude: number;
  source: string;
  highlight?: [number, number][];
}

export interface SuggestData {
  data: SuggestDataItem;
  coords: L.LatLng;
}

export type MarkerSize = 'small' | 'medium' | 'large';

interface MarkerBaseProps extends CrzpCoordinates {
  selected?: boolean;
  innerTitle?: string;
  type?: MarkerType;
  onDragEnd?: (newCoords: Wgs84Coordinates) => void;
  draggable?: boolean;
  size?: MarkerSize;
  customIcon?: L.DivIcon;
  markerLeafletOptions?: L.MarkerOptions;
}

export type MarkerProps = MarkerBaseProps;

export interface MarkersProps<DATA> {
  markers: MarkerBaseProps[];
  popupConfig?: MarkerPopupConfig<DATA>;
}

export type MarkerLayer = L.LayerGroup;

export interface CrzpMarker extends MarkerBaseProps {
  name?: string;
}
